/**
 * Google Publisher Tag (GPT) Loader Module
 * Handles loading and initialization of GPT library
 */

export class GPTLoader {
  private static instance: GPTLoader;
  private loaded: boolean = false;
  private loadPromise: Promise<void> | null = null;
  public enableDebugLog: boolean = false;

  private constructor() {}

  /**
   * Logs messages to console if debug logging is enabled
   * @param args Arguments to pass to console.log
   */
  private log(...args: any[]): void {
    if (this.enableDebugLog) {
      console.log('[Ad Handler GPT]', ...args);
    }
  }

  public static getInstance(): GPTLoader {
    if (!GPTLoader.instance) {
      GPTLoader.instance = new GPTLoader();
    }
    return GPTLoader.instance;
  }

  /**
   * Loads the GPT library from Google's servers
   */
  public async loadGPT(): Promise<void> {
    if (this.loaded) {
      return Promise.resolve();
    }

    if (this.loadPromise) {
      return this.loadPromise;
    }

    this.loadPromise = new Promise<void>((resolve, reject) => {
      // Check if GPT is already loaded
      if (window.googletag && window.googletag.apiReady) {
        this.loaded = true;
        resolve();
        return;
      }

      // Initialize googletag command queue
      window.googletag = window.googletag || {};
      window.googletag.cmd = window.googletag.cmd || [];

      // Create and append GPT script
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js';
      
      script.onload = () => {
        this.loaded = true;
        this.log('GPT library loaded successfully');
        resolve();
      };

      script.onerror = () => {
        reject(new Error('Failed to load GPT library'));
      };

      document.head.appendChild(script);
    });

    return this.loadPromise;
  }

  /**
   * Ensures GPT is ready and executes a callback
   */
  public ready(callback: () => void): void {
    if (window.googletag && window.googletag.cmd) {
      window.googletag.cmd.push(callback);
    } else {
      // If googletag doesn't exist yet, wait for it
      this.loadGPT().then(() => {
        if (window.googletag && window.googletag.cmd) {
          window.googletag.cmd.push(callback);
        }
      });
    }
  }

  /**
   * Returns whether GPT is loaded
   */
  public isLoaded(): boolean {
    return this.loaded;
  }
}
